import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Search, Eye, UserCheck, UserX, Users, Edit, Trash2, UsersIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/admin/EmptyState";
import { motion } from "framer-motion";
import { ProfissionalEditModal } from "@/components/admin/ProfissionalEditModal";
import { PacientesListModal } from "@/components/admin/PacientesListModal";
import { DeleteProfessionalModal } from "@/components/admin/DeleteProfessionalModal";
import { BulkDeleteProfessionalsModal } from "@/components/admin/BulkDeleteProfessionalsModal";
import { BulkDeletePatientsModal } from "@/components/admin/BulkDeletePatientsModal";
import { Checkbox } from "@/components/ui/checkbox";

interface Profissional {
  id: string;
  full_name: string | null;
  cpf: string | null;
  health_area: string | null;
  status: string;
  created_at: string;
  total_patients?: number;
  total_documents?: number;
  total_results?: number;
}

const Profissionais = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profissionais, setProfissionais] = useState<Profissional[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingProfissional, setEditingProfissional] = useState<string | null>(null);
  const [viewingPatients, setViewingPatients] = useState<{ id: string; name: string } | null>(null);
  const [deletingProfessional, setDeletingProfessional] = useState<{
    id: string;
    full_name: string | null;
    total_patients: number;
    total_documents: number;
    total_results: number;
  } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeletingPatients, setBulkDeletingPatients] = useState(false);

  useEffect(() => {
    loadProfissionais();
  }, []);

  const loadProfissionais = async () => {
    try {
      setLoading(true);
      const { data: profiles, error: profilesError } = await supabase
      .from("profiles")
      .select("*")
      .order("full_name", { ascending: true });

      if (profilesError) throw profilesError;

      // Buscar estatísticas de cada profissional
      const profissionaisComStats = await Promise.all(
        (profiles || []).map(async (prof) => {
          // Buscar IDs dos pacientes primeiro
          const { data: patients } = await supabase
            .from("patients_onboarding")
            .select("id")
            .eq("owner_id", prof.id);
          
          const patientIds = patients?.map(p => p.id) || [];
          
          const [patientsRes, docsRes, resultsRes] = await Promise.all([
            supabase.from("patients_onboarding").select("id", { count: "exact", head: true }).eq("owner_id", prof.id),
            supabase.from("documents_onboarding").select("id", { count: "exact", head: true }).eq("owner_id", prof.id),
            patientIds.length > 0 
              ? supabase.from("lab_results_onboarding").select("id", { count: "exact", head: true }).in("patient_id", patientIds)
              : Promise.resolve({ count: 0 })
          ]);

          return {
            ...prof,
            total_patients: patientsRes.count || 0,
            total_documents: docsRes.count || 0,
            total_results: resultsRes.count || 0
          };
        })
      );

      setProfissionais(profissionaisComStats);
    } catch (error) {
      console.error("Erro ao carregar profissionais:", error);
      toast.error("Erro ao carregar profissionais");
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (id: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const { error } = await supabase
        .from("profiles")
        .update({ status: newStatus })
        .eq("id", id);

      if (error) throw error;

      toast.success(`Profissional ${newStatus === "active" ? "ativado" : "desativado"} com sucesso`);
      loadProfissionais();
    } catch (error) {
      console.error("Erro ao atualizar status:", error);
      toast.error("Erro ao atualizar status");
    }
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set());
      setSelectAll(false);
    } else {
      const filtered = profissionais.filter((p) => {
        const search = searchTerm.toLowerCase();
        return (
          p.full_name?.toLowerCase().includes(search) ||
          p.cpf?.toLowerCase().includes(search) ||
          p.health_area?.toLowerCase().includes(search)
        );
      });
      setSelectedIds(new Set(filtered.map(p => p.id)));
      setSelectAll(true);
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const getSelectedProfessionals = () => {
    return profissionais
      .filter(p => selectedIds.has(p.id))
      .map(p => ({
        id: p.id,
        full_name: p.full_name,
        total_patients: p.total_patients || 0,
        total_documents: p.total_documents || 0,
        total_results: p.total_results || 0
      }));
  };

  const getTotalPatients = () => {
    return getSelectedProfessionals()
      .reduce((sum, prof) => sum + (prof.total_patients || 0), 0);
  };

  const filteredProfissionais = profissionais.filter((p) => {
    const search = searchTerm.toLowerCase();
    return (
      p.full_name?.toLowerCase().includes(search) ||
      p.cpf?.toLowerCase().includes(search) ||
      p.health_area?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <motion.div 
      className="space-y-6"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Profissionais</h1>
        <p className="text-muted-foreground">
          Gerenciar cadastros de profissionais de saúde
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Profissionais</CardTitle>
          <CardDescription>
            {profissionais.length} profissional{profissionais.length !== 1 ? 'is' : ''} cadastrado{profissionais.length !== 1 ? 's' : ''}
            {selectedIds.size === 0 && (
              <span className="block mt-1 text-xs text-muted-foreground">
                💡 Selecione profissionais para excluir pacientes ou profissionais em lote
              </span>
            )}
          </CardDescription>
          {selectedIds.size > 0 && (
            <div className="mt-4 flex gap-2">
              <Button
                variant="outline"
                className="border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/20"
                onClick={() => setBulkDeletingPatients(true)}
              >
                <UsersIcon className="h-4 w-4 mr-2" />
                Excluir Pacientes ({getTotalPatients()})
              </Button>
              <Button
                variant="destructive"
                onClick={() => setBulkDeleting(true)}
                className="gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Excluir {selectedIds.size} profissional{selectedIds.size !== 1 ? 'is' : ''}
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome, CPF ou área..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {filteredProfissionais.length > 0 ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectAll}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Selecionar todos"
                      />
                    </TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>CPF</TableHead>
                    <TableHead>Área</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right w-32">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProfissionais.map((prof) => (
                    <TableRow key={prof.id} className="hover:bg-muted/50 transition-colors">
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(prof.id)}
                          onCheckedChange={() => toggleSelect(prof.id)}
                          aria-label={`Selecionar ${prof.full_name || 'profissional'}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {prof.full_name || "N/A"}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {prof.cpf || "N/A"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{prof.health_area || "N/A"}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={prof.status === "active" ? "default" : "secondary"}>
                          {prof.status === "active" ? "Ativo" : "Inativo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/admin/cadastros/profissionais/${prof.id}`)}
                            title="Ver detalhes"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setViewingPatients({ id: prof.id, name: prof.full_name || 'Profissional' })}
                            title="Ver pacientes"
                          >
                            <Users className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/admin/cadastros/profissionais/${prof.id}`)}
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleStatus(prof.id, prof.status)}
                            title={prof.status === "active" ? "Desativar" : "Ativar"}
                          >
                            {prof.status === "active" ? (
                              <UserX className="h-4 w-4 text-destructive" />
                            ) : (
                              <UserCheck className="h-4 w-4 text-green-600" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeletingProfessional({
                              id: prof.id,
                              full_name: prof.full_name,
                              total_patients: prof.total_patients || 0,
                              total_documents: prof.total_documents || 0,
                              total_results: prof.total_results || 0
                            })}
                            title="Excluir permanentemente"
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <EmptyState
              icon={Users}
              title={searchTerm ? "Nenhum profissional encontrado" : "Nenhum profissional cadastrado"}
              description={searchTerm ? "Tente ajustar os filtros de busca" : "Os profissionais cadastrados aparecerão aqui"}
            />
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      {editingProfissional && (
        <ProfissionalEditModal
          open={!!editingProfissional}
          onOpenChange={(open) => !open && setEditingProfissional(null)}
          profissionalId={editingProfissional}
          onSuccess={() => {
            loadProfissionais();
            setEditingProfissional(null);
          }}
        />
      )}

      {viewingPatients && (
        <PacientesListModal
          open={!!viewingPatients}
          onOpenChange={(open) => !open && setViewingPatients(null)}
          profissionalId={viewingPatients.id}
          profissionalName={viewingPatients.name}
        />
      )}

      {deletingProfessional && (
        <DeleteProfessionalModal
          open={!!deletingProfessional}
          onOpenChange={(open) => !open && setDeletingProfessional(null)}
          professional={deletingProfessional}
          onSuccess={() => {
            loadProfissionais();
            setDeletingProfessional(null);
          }}
        />
      )}

      {bulkDeleting && (
        <BulkDeleteProfessionalsModal
          open={bulkDeleting}
          onOpenChange={setBulkDeleting}
          professionals={getSelectedProfessionals()}
          onSuccess={() => {
            loadProfissionais();
            setSelectedIds(new Set());
            setSelectAll(false);
            setBulkDeleting(false);
          }}
        />
      )}

      {bulkDeletingPatients && (
        <BulkDeletePatientsModal
          open={bulkDeletingPatients}
          onOpenChange={setBulkDeletingPatients}
          professionals={getSelectedProfessionals()}
          onSuccess={() => {
            loadProfissionais();
            setSelectedIds(new Set());
            setSelectAll(false);
            setBulkDeletingPatients(false);
          }}
        />
      )}
    </motion.div>
  );
};

export default Profissionais;